import { useCallback, useState } from 'react'
import { Empty, ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Dialog } from '../../components/Dialog'
import { ApiError, type AccountSummary, type PasswordPolicy, accounts, platform, session } from '../../lib/api'
import { passwordAgeLabel } from '../../lib/passwordAge'
import { PasswordPolicyCard } from './PasswordPolicyCard'

const ASSIGNABLE = ['owner', 'administrator', 'analyst', 'viewer'] as const

const ROLE_DESCRIPTIONS: Record<string, string> = {
  owner: 'Everything, including managing these accounts.',
  administrator: 'Authors and publishes policy, manages devices and dictionaries.',
  analyst: 'Triages alerts and queries events. Changes nothing on a device.',
  viewer: 'Reads. Changes nothing.'
}

/**
 * Who can sign in to this organization.
 *
 * Owner-only, and hidden from everybody else - not because the browser is trusted with that decision,
 * but because showing an analyst a screen whose every button answers 403 wastes their time. The role
 * is checked out here, before the component holding the hooks exists, so a viewer opening this route
 * makes no request at all.
 *
 * There is no delete. An account is disabled, which stops it signing in and keeps its name against
 * everything it did; deleting it would leave the alerts it acknowledged and the policies it approved
 * attributed to nobody. The last active owner can be neither disabled nor demoted, because the screen
 * that grants the role back is this one.
 */
export function AccountListPage() {
  const identity = session.identity()
  if (identity?.roles.includes('owner')) return <OwnerAccountList />

  // A platform administrator inside this organization. Their support session stops below owner and
  // cannot manage accounts (ADR-S-043); the platform session parked behind it can, for the acts that
  // get somebody back in, so the screen is offered on that authority rather than the session's.
  if (identity?.assumedByName && session.parked()) {
    return <SupportAccountList tenantId={identity.tenantId} tenantName={identity.tenantName ?? identity.tenantSlug ?? 'this organization'} />
  }

  return <NotYours />
}

/**
 * What a row can do, and on whose authority.
 *
 * One row component for both screens rather than a second copy, because the facts a row states -
 * locked, disabled, never signed in, password age - are the same whoever is looking, and two copies
 * would drift on exactly the details that make a lockout legible. What differs is which endpoints the
 * buttons call, and whether roles can be changed at all.
 */
interface AccountOperations {
  resetPassword: (accountId: string, newPassword: string) => Promise<unknown>
  unlock: (accountId: string) => Promise<unknown>
  setStatus: (accountId: string, status: string) => Promise<unknown>
  /** Absent for support: changing who may do what belongs to the organization's owners. */
  setRoles?: (accountId: string, roles: string[]) => Promise<unknown>
}

const OWNER_OPERATIONS: AccountOperations = {
  resetPassword: accounts.resetPassword,
  unlock: accounts.unlock,
  setStatus: accounts.setStatus,
  setRoles: accounts.setRoles
}

/**
 * The platform token, read at the moment of each call rather than captured once, so a platform
 * session that renewed while this screen was open is the one used.
 */
function platformToken(): string | undefined {
  return session.parked()?.token
}

function supportOperations(tenantId: string): AccountOperations {
  return {
    resetPassword: (accountId, newPassword) =>
      platform.resetTenantAccountPassword(tenantId, accountId, newPassword, platformToken()),
    unlock: (accountId) => platform.unlockTenantAccount(tenantId, accountId, platformToken()),
    setStatus: (accountId, status) => platform.setTenantAccountStatus(tenantId, accountId, status, platformToken())
  }
}

function NotYours() {
  return (
    <div className="page-header">
      <div>
        <h1>Accounts</h1>
        <p>
          Managing accounts belongs to an owner. Ask an owner of this organization if you need
          somebody added, removed, or given a different role.
        </p>
      </div>
    </div>
  )
}

function OwnerAccountList() {
  const [reloadToken, setReloadToken] = useState(0)
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)
  const [added, setAdded] = useState<string | null>(null)
  const reload = useCallback(() => setReloadToken((token) => token + 1), [])
  const list = useAsync(() => accounts.list(search || undefined), [search, reloadToken])
  const assumptions = useAsync(() => accounts.assumptions(), [reloadToken])
  // Read once for the whole list rather than per row: it is one setting for the organization, and a
  // request per person would be a request per person.
  const policy = useAsync(() => accounts.passwordPolicy(), [reloadToken])

  /**
   * Shows the account that was just created, by searching for it.
   *
   * The list is capped, and this organization reached fifty-five accounts - so a new one sorted by
   * name could land past the cap and simply not appear, on the screen whose whole job is to say who
   * can sign in. The same fix the organizations list took, for the same reason.
   */
  const onCreated = useCallback(
    (email: string) => {
      setSearch(email)
      setAdded(email)
      setAdding(false)
      reload()
    },
    [reload]
  )

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Accounts</h1>
          <p>Who can sign in to this organization, and what they are allowed to do.</p>
        </div>
      </div>

      <ErrorBanner message={list.error} />

      {/*
        Said in words, because the list below is filtered to the new person and an operator who did
        not notice the search box change would be looking at a one-row table wondering where everyone
        went. The sentence explains both halves of what just happened.
      */}
      {added && (
        <div className="banner success" role="status" data-testid="account-added">
          {added} was added, and the list below is filtered to them. Clear the search to see everybody.
        </div>
      )}

      <CreateAccountDialog open={adding} onClose={() => setAdding(false)} onCreated={onCreated} />

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>People</h3>

          <div className="toolbar">
            <div className="field grow">
              <label htmlFor="account-search">Search</label>
              <input
                id="account-search"
                data-testid="account-search"
                value={search}
                placeholder="A name or an email address"
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <button type="button" className="primary" onClick={() => setAdding(true)} data-testid="add-account">
              Add somebody
            </button>
          </div>

          {/*
            What this list is actually showing.

            It was drawing one page of fifty and saying nothing, so an organization with fifty-one
            people showed fifty of them and looked complete. A list that cannot show everything has to
            say so, or it is not a list of who can sign in - it is a list of some of them (D-263).
          */}
          {list.value && (
            <p className="muted" data-testid="account-count">
              {list.value.total === list.value.items.length
                ? `${list.value.total} ${list.value.total === 1 ? 'person' : 'people'}.`
                : `Showing ${list.value.items.length} of ${list.value.total}. Search to narrow it.`}
            </p>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table data-testid="account-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last signed in</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.value?.items.map((account) => (
                  <AccountRow
                    key={account.accountId}
                    account={account}
                    policy={policy.value}
                    onChanged={reload}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {list.value && list.value.items.length === 0 && (
            <Empty>{list.loading ? 'Loading…' : 'Nobody can sign in to this organization.'}</Empty>
          )}
        </div>
      </div>

      {/*
        Below the list rather than above it. It is a rule about everybody on this screen, but nobody
        comes here to set it - they come to find a person - and a settings box above a list is a box
        in the way of the list.
      */}
      <PasswordPolicyCard onChanged={reload} />

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Support access</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Every time somebody who runs this deployment opened your organization, and why. You do
            not have to ask anybody for this list.
          </p>
          <ErrorBanner message={assumptions.error} />
          {assumptions.value && assumptions.value.items.length === 0 ? (
            // Stated, never implied. A blank space here and "nobody has ever done this" are
            // indistinguishable, and they are very different facts to be looking at.
            <p data-testid="no-assumptions">Nobody from the platform has opened this organization.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table data-testid="assumption-table">
                <thead>
                  <tr>
                    <th>Who</th>
                    <th>As</th>
                    <th>Why</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {assumptions.value?.items.map((entry) => (
                    <tr key={entry.assumptionId}>
                      <td>
                        {entry.platformName}
                        <br />
                        <span className="muted">{entry.platformEmail}</span>
                      </td>
                      <td>{entry.roles.join(', ')}</td>
                      <td>{entry.reason}</td>
                      <td>{formatUtc(entry.startedUtc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * The Accounts screen as support sees it: the people in this organization, and the three things that
 * get one of them back in.
 *
 * Deliberately narrower than an owner's. No adding people, no roles, no password policy and no
 * support-access history - an organization's owners decide who may do what, and the list of who from
 * the platform opened the organization is theirs to read rather than ours. Every act offered here
 * happens inside the support session that is open, which the owner's Support access list shows with
 * its reason.
 */
function SupportAccountList({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const [reloadToken, setReloadToken] = useState(0)
  const [search, setSearch] = useState('')
  const reload = useCallback(() => setReloadToken((token) => token + 1), [])
  const list = useAsync(
    () => platform.listTenantAccounts(tenantId, search || undefined, platformToken()),
    [tenantId, search, reloadToken]
  )
  const operations = supportOperations(tenantId)

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Accounts</h1>
          <p>Who can sign in to {tenantName}.</p>
        </div>
      </div>

      {/*
        Said once, above the list, because the screen looks like an owner's and is not one. Somebody
        wondering where the role controls went should be told rather than left to assume a defect.
      */}
      <div className="banner" role="note" data-testid="support-accounts-note">
        You are here as platform support. You can reset a password, unlock an account or enable a
        disabled one. Adding people and changing roles stay with {tenantName}&rsquo;s owners.
      </div>

      <ErrorBanner message={list.error} />

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>People</h3>

          <div className="toolbar">
            <div className="field grow">
              <label htmlFor="account-search">Search</label>
              <input
                id="account-search"
                data-testid="account-search"
                value={search}
                placeholder="A name or an email address"
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          {list.value && (
            <p className="muted" data-testid="account-count">
              {list.value.total === list.value.items.length
                ? `${list.value.total} ${list.value.total === 1 ? 'person' : 'people'}.`
                : `Showing ${list.value.items.length} of ${list.value.total}. Search to narrow it.`}
            </p>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table data-testid="account-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last signed in</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.value?.items.map((account) => (
                  <AccountRow
                    key={account.accountId}
                    account={account}
                    policy={null}
                    operations={operations}
                    onChanged={reload}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {list.value && list.value.items.length === 0 && (
            <Empty>{list.loading ? 'Loading…' : 'Nobody can sign in to this organization.'}</Empty>
          )}
        </div>
      </div>
    </>
  )
}

function AccountRow({
  account,
  policy,
  operations = OWNER_OPERATIONS,
  onChanged
}: {
  account: AccountSummary
  /** The organization's password policy, so an age can be read as a deadline rather than a number. */
  policy: PasswordPolicy | null
  /** Whose endpoints the buttons call. An owner's by default. */
  operations?: AccountOperations
  onChanged: () => void
}) {
  const setRoles = operations.setRoles
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  /**
   * Which of the two irreversible-from-here changes is being asked about.
   *
   * Both applied on the control. A select whose options are one row apart set somebody's role on the
   * way past, and the Disable button sits where the eye lands after reading the row it belongs to,
   * with a button of the same size beside it. Neither is recoverable by the person it happens to:
   * they are signed out of the console and cannot ask this screen to put it back.
   */
  const [asking, setAsking] = useState<{ kind: 'role'; role: string } | { kind: 'disable' } | null>(null)
  const locked = account.lockedUntilUtc !== null && new Date(account.lockedUntilUtc) > new Date()
  const age = passwordAgeLabel(
    account.passwordChangedUtc,
    policy?.maxAgeDays ?? 0,
    policy?.warnDays ?? 0,
    Date.now()
  )

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setProblem(null)
    try {
      await work()
      onChanged()
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'That could not be done.')
    } finally {
      setBusy(false)
    }
  }

  const reset = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setProblem(null)
    try {
      await operations.resetPassword(account.accountId, newPassword)
      setResetting(false)
      setNewPassword('')
      onChanged()
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'The password could not be reset.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <tr data-testid={`account-${account.email}`}>
      <td>
        {account.displayName}
        <br />
        <span className="muted">{account.email}</span>
        {/* Not while the dialog is open: it renders the same message beside the field that caused
            it, and two copies of one refusal reads as two things having gone wrong. */}
        {problem && !resetting && (
          <p className="error" role="alert" data-testid="account-problem">
            {problem}
          </p>
        )}
      </td>
      <td>
        {/* Read-only where roles cannot be changed from here, rather than a control that is disabled
            without saying why. */}
        {!setRoles ? (
          <span data-testid={`role-${account.email}`}>{account.roles.join(', ')}</span>
        ) : (
        <select
          value={account.roles[0] ?? ''}
          disabled={busy}
          aria-label={`Role for ${account.displayName}`}
          onChange={(event) => setAsking({ kind: 'role', role: event.target.value })}
          data-testid={`role-${account.email}`}
        >
          {/* Only the four this organization can assign. The platform role is not offered, and is
              refused by the server if it is asked for anyway. */}
          {ASSIGNABLE.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
        )}
      </td>
      <td>
        {account.status === 'active' ? 'Active' : 'Disabled'}
        {locked && (
          <>
            <br />
            <span className="muted" data-testid="locked-until">
              Locked until {formatUtc(account.lockedUntilUtc)}
            </span>
          </>
        )}
        {account.mustChangePassword && (
          <>
            <br />
            <span className="muted">Has not chosen a password yet</span>
          </>
        )}
        {/*
          Stated rather than left blank, including when it is not known: an account from before this
          was recorded never expires, and that is a fact worth reading rather than a gap to
          interpret. The same rule as "Never signed in" one cell over.
        */}
        <br />
        <span className={age.tone === 'muted' ? 'muted' : age.tone === 'warn' ? 'warn' : 'error'}>
          {age.text}
        </span>
      </td>
      {/* Never a blank cell: somebody who has never signed in is a fact worth reading. */}
      <td>{account.lastSignInUtc ? formatUtc(account.lastSignInUtc) : 'Never signed in'}</td>
      <td>
        {/*
          Only while the account is actually locked. A lockout is a third fact about a row that
          already shows two - an account can be active *and* locked - so it cannot be folded into
          Enable/Disable without that one word meaning two different things. An inert control on
          every row would be noise on the screen whose whole job is to state facts.

          No confirmation: only the damaging direction asks, and this is the recovery.
        */}
        {locked && (
          <>
            <button
              type="button"
              disabled={busy}
              data-testid={`unlock-${account.email}`}
              onClick={() => run(() => operations.unlock(account.accountId))}
            >
              Unlock
            </button>{' '}
          </>
        )}
        {/* Only disabling asks. Enabling gives somebody their console back, which is the recovery
            rather than the damage, and a question in front of it would be a question in front of
            undoing the mistake this one exists to prevent. */}
        <button
          type="button"
          disabled={busy}
          data-testid={`status-${account.email}`}
          onClick={() =>
            account.status === 'active'
              ? setAsking({ kind: 'disable' })
              : run(() => operations.setStatus(account.accountId, 'active'))
          }
        >
          {account.status === 'active' ? 'Disable' : 'Enable'}
        </button>{' '}
        <button
          type="button"
          onClick={() => setResetting((open) => !open)}
          disabled={busy}
          data-testid={`reset-${account.email}`}
        >
          Reset password
        </button>
        {/*
          Still not a browser dialog, and for the same reason as before: the password has to be read
          back to somebody, and a prompt box cannot be corrected, cannot be re-read, and blocks the
          page it belongs to. What changed is that the console now has a dialog of its own, which has
          none of those faults - it holds a real input somebody can correct, keeps the refusal beside
          the field, and leaves the page working underneath.

          It moved out of the row because a form unfolding inside a table cell reflows every row below
          it, so the person somebody was aiming at moves as they reach for it.
        */}
        <Dialog
          open={resetting}
          title={`Reset the password for ${account.displayName}`}
          onClose={() => setResetting(false)}
          data-testid={`reset-dialog-${account.email}`}
        >
          <form onSubmit={reset}>
            <label htmlFor={`reset-password-${account.accountId}`}>
              A new password for {account.displayName}. They must replace it at first use.
            </label>
            {/* Readable as it is typed, for the reason the create field states: whoever resets
                a password has to say it out loud to the person it belongs to, and it cannot survive
                first use either way. `off` rather than `new-password` because this is somebody
                else's secret and the browser would file it under the administrator's own login. */}
            <input
              id={`reset-password-${account.accountId}`}
              type="text"
              autoComplete="off"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              data-testid={`reset-password-${account.email}`}
              required
            />

            {problem && (
              <p className="error" role="alert" data-testid={`reset-problem-${account.email}`}>
                {problem}
              </p>
            )}

            <div className="dialog-actions">
              <button type="button" onClick={() => setResetting(false)} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="primary" disabled={busy} data-testid={`confirm-reset-${account.email}`}>
                Set password
              </button>
            </div>
          </form>
        </Dialog>

        <ConfirmDialog
          open={asking?.kind === 'role'}
          title={`Change the role for ${account.displayName}`}
          confirmLabel={asking?.kind === 'role' ? `Make them ${asking.role}` : 'Change it'}
          busy={busy}
          onConfirm={() => {
            if (asking?.kind !== 'role') return
            const role = asking.role
            setAsking(null)
            if (setRoles) void run(() => setRoles(account.accountId, [role]))
          }}
          onClose={() => setAsking(null)}
          data-testid={`role-confirm-${account.email}`}
        >
          <p>
            {account.email} goes from {account.roles[0] ?? 'no role'} to{' '}
            {asking?.kind === 'role' ? asking.role : ''} the next time they load a page. Whatever the
            old role let them do and the new one does not, they stop being able to do.
          </p>
        </ConfirmDialog>

        <ConfirmDialog
          open={asking?.kind === 'disable'}
          title={`Disable ${account.displayName}`}
          confirmLabel="Disable them"
          danger
          busy={busy}
          onConfirm={() => {
            setAsking(null)
            void run(() => operations.setStatus(account.accountId, 'disabled'))
          }}
          onClose={() => setAsking(null)}
          data-testid={`disable-confirm-${account.email}`}
        >
          <p>
            {account.email} can no longer sign in. Nothing they did is removed and the account keeps
            its name against everything it signed, so the audit trail still reads. Enabling them again
            is one button on this row — but only somebody who can still sign in can press it.
          </p>
        </ConfirmDialog>
      </td>
    </tr>
  )
}

/**
 * Adding somebody, in a dialog rather than a card that is always on the screen.
 *
 * It used to sit above the list, so the first thing an owner saw on the screen that answers "who can
 * sign in" was a form for adding somebody new - which is the rarest thing done here, above the
 * commonest. The button is in the list's own toolbar now, next to the search that is the other thing
 * anybody does with this table.
 *
 * A refusal keeps the dialog open. The server is where the password and role rules live
 * (`PasswordHashing.Problems`, `AccountRules.Problems`), so the first somebody hears of a password
 * that is too short is a round trip - and closing over four filled-in fields to report it would make
 * them type the whole thing again.
 */
function CreateAccountDialog({
  open,
  onClose,
  onCreated
}: {
  open: boolean
  onClose: () => void
  onCreated: (email: string) => void
}) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<string>('viewer')
  const [password, setPassword] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setProblem(null)
    try {
      await accounts.create(email, displayName || email, [role], password)
      // The address is handed back so the list can show the person who was just added, rather than
      // reloading a capped page they may not be on.
      const created = email
      setEmail('')
      setDisplayName('')
      setPassword('')
      setRole('viewer')
      setProblem(null)
      onCreated(created)
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'The account could not be created.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} title="Add somebody" onClose={onClose} data-testid="create-account-dialog">
      <p className="muted" style={{ marginTop: 0 }}>
        You choose their first password and tell them what it is. They have to replace it before they
        can use the console, so it never stays a password two people know.
      </p>
      <form onSubmit={submit} data-testid="create-account-form">
        <label htmlFor="new-email">Email address</label>
        <p className="hint" id="new-email-hint">
          Their sign-in name, alongside this organization&rsquo;s ID.
        </p>
        <input
          id="new-email"
          type="email"
          aria-describedby="new-email-hint"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />

        <label htmlFor="new-name">Full name</label>
        {/* A hint rather than a placeholder, so it is still readable once there is a name in the
            field - which is when somebody checks they filled in the right box. */}
        <p className="hint" id="new-name-hint">Shown against anything they do. Optional.</p>
        <input
          id="new-name"
          aria-describedby="new-name-hint"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />

        <label htmlFor="new-role">Role</label>
        <select id="new-role" value={role} onChange={(event) => setRole(event.target.value)}>
          {ASSIGNABLE.map((option) => (
            <option key={option} value={option}>
              {option} &mdash; {ROLE_DESCRIPTIONS[option]}
            </option>
          ))}
        </select>

        <label htmlFor="new-password">First password</label>
        {/* Deliberately readable as it is typed. Whoever creates the account has to say it out loud
            to the person it belongs to, and a masked field they cannot check is how a password gets
            handed over wrong. It cannot survive first use either way. */}
        <input
          id="new-password"
          type="text"
          autoComplete="off"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />

        {/* Inside the dialog, beside the fields it is about. A refusal rendered on the page behind
            would be hidden by the thing that caused it. */}
        {problem && (
          <p className="error" role="alert" data-testid="create-account-error">
            {problem}
          </p>
        )}

        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create account'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
